import * as Yup from "yup"

/**
 * Returns the schema for validating a visit report.
 * @returns {Yup.ObjectSchema} The Yup object schema for visit report validation.
 */
export const getVisitReportSchema = ({
  ERROR_MSG_REQUIRED = "This field is required",
  VISIT_REPORT_ERROR_MSG_ATTENDANCE_START = "Invalid attendance start time",
  VISIT_REPORT_ERROR_MSG_OCCASIONS = "Please select at least one occasion",
  VISIT_REPORT_ERROR_MSG_MAIL_RECIPIENTS = "Invalid email recipients",
  VISIT_REPORT_ERROR_MSG_FILE_SIZE = "Total file size exceeds the limit",
  VISIT_REPORT_ERROR_MSG_FILE_EXTENSION = "Invalid file extension",
} = {}) => {
  return Yup.object().shape({
    attendanceAt: Yup.string().required(ERROR_MSG_REQUIRED),
    attendanceStart: Yup.string()
      .nullable()
      .test("is-before", function (value) {
        if (!value) return true
        if (!this.parent?.attendanceEnd) return true
        const { attendanceEnd } = this.parent
        const startTime = new Date(value)
        const endTime = new Date(attendanceEnd)
        return startTime < endTime || this.createError({ message: VISIT_REPORT_ERROR_MSG_ATTENDANCE_START })
      }),
    contactName: Yup.string(),
    customerNumber: Yup.string().required(ERROR_MSG_REQUIRED),
    occasions: Yup.array().min(1, VISIT_REPORT_ERROR_MSG_OCCASIONS),
    mailRecipients: Yup.string()
      .test("is-email", VISIT_REPORT_ERROR_MSG_MAIL_RECIPIENTS, function (value) {
        const emails = value.split(",")
        for (let email of emails) {
          if (!Yup.string().email().isValidSync(email.trim())) {
            return false
          }
        }
        return true
      })
      .nullable(),
    meetingName: Yup.string().required(ERROR_MSG_REQUIRED),
    attachments: Yup.array()
      .of(
        Yup.object().shape({
          size: Yup.number(),
          mimeType: Yup.string(),
          url: Yup.string(),
          asset: Yup.object(),
          name: Yup.string().test("fileExtension", VISIT_REPORT_ERROR_MSG_FILE_EXTENSION, function (value) {
            const allowedExtensions = [".jpg", ".jpeg", ".png", ".mp4", ".pdf", ".xlsx", ".xls", ".csv", ".docx"]
            const fileExtension = value.substring(value.lastIndexOf("."))
            return allowedExtensions.includes(fileExtension)
          }),
        }),
      )
      .test("fileSize", VISIT_REPORT_ERROR_MSG_FILE_SIZE, (attachments) => {
        let totalSize = 0
        for (const attachment of attachments) {
          totalSize += attachment.size
        }
        return totalSize <= 10485760
      }),
    deletedAttachmentReferences: Yup.array(),
    attachmentReferences: Yup.array().of(
      Yup.object().shape({
        size: Yup.number(),
        mimeType: Yup.string(),
        url: Yup.string(),
        asset: Yup.object(),
        name: Yup.string().test("fileExtension", VISIT_REPORT_ERROR_MSG_FILE_EXTENSION, function (value) {
          const allowedExtensions = [".jpg", ".jpeg", ".png", ".mp4", ".pdf", ".xlsx", ".xls", ".csv", ".docx"]
          const fileExtension = value.substring(value.lastIndexOf("."))
          return allowedExtensions.includes(fileExtension)
        }),
      }),
    ),
  })
}
